// src/components/PaymentStep.js
import React, { useState, useEffect, useRef } from 'react';
import { Lock, AlertCircle, CreditCard } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { doc, setDoc, deleteDoc, addDoc, collection, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';

// ✅ Charger Stripe avec la clé publique
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Bug connu Safari desktop (macOS 26.x) : le remplissage automatique d'une carte enregistrée
// fait planter le moteur WebKit → la page recharge et la réservation est perdue. Non corrigé par
// Apple, impossible à empêcher depuis le site. iPhone/iOS = OK, Chrome/Firefox = OK.
// → On avertit UNIQUEMENT les utilisateurs Safari sur Mac (les seuls concernés).
const isMacDesktopSafari = (() => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /Macintosh/.test(ua)
    && /Safari/.test(ua)
    && !/Chrome|CriOS|Chromium|Edg|OPR|Firefox|FxiOS/.test(ua)
    && !/iPhone|iPad|iPod/.test(ua);
})();

const DEMO_MODE = false;
const API_URL = process.env.REACT_APP_API_URL || 'https://valetier.maisonceronne.com/api';
const SUPPORT_PHONE = '+33 7 63 54 71 17';

// Sans caractères ambigus au téléphone (0/O, 1/I/L)
const ERROR_CODE_CHARS = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
function generateErrorCode() {
  let code = '';
  for (let i = 0; i < 6; i++) code += ERROR_CODE_CHARS[Math.floor(Math.random() * ERROR_CODE_CHARS.length)];
  return code;
}

// Enregistre un incident de paiement silencieux (Stripe qui ne charge jamais, crash JS…)
// avec un code court que le client peut lire au téléphone — consultable dans
// Paramètres → Liste d'attente & Relances → Erreurs paiement.
// Nombre d'iframes Stripe présentes dans la page au moment du rapport — permet de
// distinguer "Stripe.js n'a jamais démarré" (0) de "démarré mais planté en cours" (>0).
function countStripeIframes() {
  try {
    return document.querySelectorAll('iframe[src*="js.stripe.com"], iframe[name*="__privateStripeFrame"]').length;
  } catch (e) {
    return -1;
  }
}

async function reportPaymentError({ code, step, errorMessage, guestInfo, consoleLog }) {
  const finalCode = code || generateErrorCode();
  try {
    await addDoc(collection(db, 'payment_error_reports'), {
      code: finalCode,
      module: 'booking-light',
      step,
      errorMessage: errorMessage ? String(errorMessage).slice(0, 500) : '',
      consoleLog: consoleLog && consoleLog.length ? consoleLog.slice(-40).join('\n').slice(0, 4000) : '',
      stripeIframeCount: countStripeIframes(),
      online: navigator.onLine,
      url: window.location.href,
      userAgent: navigator.userAgent,
      language: navigator.language,
      // Détection traduction automatique : Chrome ajoute la classe "translated-ltr"
      // sur <html> quand il a traduit la page (cause connue de crash React).
      pageLang: document.documentElement.lang || '',
      pageTranslated: /translated/.test(document.documentElement.className || ''),
      guestEmail: guestInfo?.email || '',
      guestName: `${guestInfo?.firstName || ''} ${guestInfo?.name || ''}`.trim(),
      createdAt: Timestamp.now(),
      handled: false
    });
  } catch (e) {
    console.error('Erreur enregistrement rapport erreur paiement:', e);
  }
  return finalCode;
}

// Affiche le message + code en DOM pur, HORS de React : si le crash a démonté toute
// la page React (écran blanc), ce bandeau reste visible — c'est tout son intérêt.
// (Incident Valentin 2026-07-02 : le rapport partait bien en base mais le message
// React mourait avec la page, le client ne voyait rien.)
function showBlockedBanner(code, pt) {
  try {
    if (document.getElementById('payment-blocked-banner')) return;
    const div = document.createElement('div');
    div.id = 'payment-blocked-banner';
    div.setAttribute('translate', 'no');
    div.style.cssText = 'position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:2147483647;max-width:420px;width:calc(100% - 32px);background:#fff7ed;border:2px solid #fdba74;border-radius:12px;padding:16px;box-shadow:0 10px 25px rgba(0,0,0,0.2);font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;';
    const title = document.createElement('p');
    title.style.cssText = 'margin:0 0 4px;font-size:14px;font-weight:600;color:#7c2d12;';
    title.textContent = pt.blockedTitle;
    const msg = document.createElement('p');
    msg.style.cssText = 'margin:0;font-size:14px;color:#9a3412;';
    msg.textContent = pt.blockedMessage.replace('{phone}', SUPPORT_PHONE);
    const codeEl = document.createElement('p');
    codeEl.style.cssText = 'margin:8px 0 0;font-size:24px;font-weight:700;letter-spacing:0.2em;text-align:center;color:#7c2d12;';
    codeEl.textContent = code;
    div.appendChild(title);
    div.appendChild(msg);
    div.appendChild(codeEl);
    document.body.appendChild(div);
  } catch (e) { /* ignore */ }
}

const paymentTranslations = {
  fr: {
    title: 'Sécurisation de la réservation',
    paymentSummary: 'Récapitulatif de paiement',
    client: 'Client :',
    totalAmount: 'Montant total du séjour :',
    preAuthRequested: 'Pré-autorisation demandée :',
    noActualCharge: 'Sans débit effectif',
    automaticCharge: 'Débit automatique :',
    chargeDate: '35 jours avant l\'arrivée',
    bankNotice: 'Votre banque va probablement afficher 0€ ou 1€. C\'est normal ! Nous vérifions juste que votre carte est valide.',
    securePayment: 'Paiement sécurisé par Stripe',
    secureInfo: 'Vos données bancaires sont cryptées et ne sont jamais stockées sur nos serveurs',
    cardDetails: 'Informations de carte bancaire',
    noteTitle: 'Note :',
    noteText: 'Votre carte sera pré-autorisée pour {amount}€ mais ne sera débitée qu\'après confirmation ou 35 jours avant la date d\'arrivée. La pré-autorisation sera automatiquement annulée en cas de non confirmation de la réservation.',
    back: 'Retour',
    processing: 'Traitement...',
    preAuthorize: 'Pré-autoriser',
    loadingCard: 'Chargement du paiement sécurisé…',
    threeDSTitle: 'Vérification de votre paiement en cours',
    threeDSMessage: 'Votre banque peut vous demander de valider le paiement (redirection possible). Veuillez compléter la vérification pour finaliser votre réservation. Ne fermez pas cette page.',
    errorDeclined: 'Carte refusée. Veuillez vérifier vos informations ou utiliser une autre carte.',
    errorInsufficient: 'Fonds insuffisants sur la carte.',
    errorIncorrectCvc: 'Code CVC incorrect.',
    errorExpired: 'Carte expirée.',
    errorAuth: 'La vérification 3D Secure n\'a pas pu être complétée. Veuillez réessayer ou utiliser une autre carte.',
    errorConnection: 'Erreur de connexion. Veuillez réessayer.',
    errorGeneric: 'Erreur lors de la vérification de la carte. Veuillez réessayer.',
    errorIncomplete: 'Veuillez remplir toutes les informations de carte.',
    blockedTitle: 'Le paiement ne s\'affiche pas correctement',
    blockedMessage: 'Merci de nous appeler au {phone} en communiquant ce code :'
  },
  en: {
    title: 'Securing the reservation',
    paymentSummary: 'Payment summary',
    client: 'Client:',
    totalAmount: 'Total stay amount:',
    preAuthRequested: 'Pre-authorization requested:',
    noActualCharge: 'No actual charge',
    automaticCharge: 'Automatic charge:',
    chargeDate: '35 days before arrival',
    bankNotice: 'Your bank will probably display €0 or €1. This is normal! We are just verifying that your card is valid.',
    securePayment: 'Secure payment by Stripe',
    secureInfo: 'Your banking data is encrypted and never stored on our servers',
    cardDetails: 'Card information',
    noteTitle: 'Note:',
    noteText: 'Your card will be pre-authorized for €{amount} but will only be charged after confirmation or 35 days before arrival date. The pre-authorization will be automatically cancelled if the reservation is not confirmed.',
    back: 'Back',
    processing: 'Processing...',
    preAuthorize: 'Pre-authorize',
    loadingCard: 'Loading secure payment…',
    threeDSTitle: 'Payment verification in progress',
    threeDSMessage: 'Your bank may ask you to verify the payment (you may be redirected). Please complete the verification to finalize your reservation. Do not close this page.',
    errorDeclined: 'Card declined. Please check your information or use another card.',
    errorInsufficient: 'Insufficient funds on the card.',
    errorIncorrectCvc: 'Incorrect CVC code.',
    errorExpired: 'Card expired.',
    errorAuth: '3D Secure verification could not be completed. Please try again or use another card.',
    errorConnection: 'Connection error. Please try again.',
    errorGeneric: 'Error verifying the card. Please try again.',
    errorIncomplete: 'Please fill in all card information.',
    blockedTitle: 'The payment form is not displaying correctly',
    blockedMessage: 'Please call us at {phone} and give us this code:'
  }
};

// Composant interne avec accès à Stripe
function PaymentForm({
  amount,
  onPaymentSuccess,
  onPaymentError,
  onPaymentAttemptStarted,
  onBack,
  guestInfo,
  reservationData,
  language,
  pt,
  setupIntentId,
  stripeCustomerId
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const blockedReportedRef = useRef(false);
  const logBufferRef = useRef([]);
  const cardholderName = `${guestInfo.firstName} ${guestInfo.name}`.trim();

  // 🔵 Filet de sécurité : détection basée UNIQUEMENT sur des erreurs réelles détectées
  // (aucun délai/chronomètre). Capture des logs console + erreurs de chargement de
  // ressources + violations CSP, depuis le montage de cet écran — inclus dans le rapport
  // pour donner une vraie trace technique. Dès qu'une vraie erreur JS ou une promesse
  // rejetée survient (ex: script Stripe bloqué par un logiciel de sécurité bancaire),
  // un code court s'affiche immédiatement. Consultable dans Paramètres → Liste d'attente
  // & Relances → Erreurs paiement.
  useEffect(() => {
    const push = (level, args) => {
      try {
        const msg = args.map(a => {
          if (a instanceof Error) return a.message;
          if (typeof a === 'object' && a !== null) { try { return JSON.stringify(a); } catch { return String(a); } }
          return String(a);
        }).join(' ');
        logBufferRef.current.push(`[${level}] ${msg}`);
        if (logBufferRef.current.length > 40) logBufferRef.current.shift();
      } catch (e) { /* ignore */ }
    };

    const origError = console.error;
    const origWarn = console.warn;
    console.error = (...args) => { push('error', args); origError.apply(console, args); };
    console.warn = (...args) => { push('warn', args); origWarn.apply(console, args); };

    // Erreurs de chargement de ressources (script/img/iframe bloqués) — ne remontent
    // qu'en phase de capture, ne passent pas par onerror/onunhandledrejection classiques.
    const handleResourceError = (event) => {
      const target = event.target;
      if (target && target !== window && (target.src || target.href)) {
        push('resource-error', [`Ressource bloquée: ${target.src || target.href}`]);
      }
    };
    window.addEventListener('error', handleResourceError, true);

    const handleCSP = (event) => {
      push('csp-violation', [`${event.violatedDirective} bloqué pour ${event.blockedURI}`]);
    };
    document.addEventListener('securitypolicyviolation', handleCSP);

    // Déclencheur unique : une vraie erreur JS ou promesse rejetée sur cet écran.
    const handleWindowError = async (event) => {
      if (blockedReportedRef.current) return;
      blockedReportedRef.current = true;
      // Bandeau affiché AVANT l'écriture du rapport : même si Firestore est bloqué
      // ou que React est déjà mort, le client voit le code immédiatement.
      const code = generateErrorCode();
      showBlockedBanner(code, pt);
      await reportPaymentError({
        code,
        step: 'js_error',
        errorMessage: event?.error?.message || event?.reason?.message || event?.message,
        guestInfo,
        consoleLog: logBufferRef.current
      });
    };
    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleWindowError);

    return () => {
      console.error = origError;
      console.warn = origWarn;
      window.removeEventListener('error', handleResourceError, true);
      document.removeEventListener('securitypolicyviolation', handleCSP);
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleWindowError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    if (DEMO_MODE) {
      setTimeout(() => {
        console.log('🎭 Mode démo : Paiement simulé');
        onPaymentSuccess({
          id: 'demo_payment_' + Date.now(),
          amount: amount * 100,
          status: 'succeeded',
          last4: '4242'
        });
        // ✅ NE PAS remettre processing à false après succès (même en mode démo)
      }, 2000);
      return;
    }

    try {
      // 🔒 Doc de sécurité écrit AVANT le 3D Secure — c'est la source de récupération
      // utilisée par /confirm-reservation au retour d'une redirection 3DS (ou par le CRON).
      try {
        await setDoc(doc(db, 'payment_safety', setupIntentId), {
          setupIntentId: setupIntentId,
          stripeCustomerId: stripeCustomerId,
          // Données minimales (rétrocompat + lisibilité)
          guestEmail: guestInfo.email,
          guestName: `${guestInfo.firstName} ${guestInfo.name}`.trim(),
          guestPhone: guestInfo.phone || '',
          // Toutes les données de résa — permet la récupération automatique
          guestInfo: {
            name: guestInfo.name,
            firstName: guestInfo.firstName,
            email: guestInfo.email,
            phone: guestInfo.phone || '',
            company: guestInfo.company || '',
            address: guestInfo.address || '',
            city: guestInfo.city || '',
            postalCode: guestInfo.postalCode || '',
            country: guestInfo.country || 'France',
            message: guestInfo.message || ''
          },
          checkIn: reservationData?.checkIn || null,
          checkOut: reservationData?.checkOut || null,
          selectedRoomType: reservationData?.selectedRoomType || null,
          selectedRoomIds: reservationData?.selectedRoomIds || [],
          selectedRoomCount: reservationData?.selectedRoomCount || {},
          selectedOccupancy: reservationData?.selectedOccupancy || {},
          selectedRoomOccupancies: reservationData?.selectedRoomOccupancies || {},
          displayPrices: reservationData?.displayPrices || {},
          selectedOptions: reservationData?.selectedOptions || [],
          appliedCoupon: reservationData?.appliedCoupon || null,
          total: reservationData?.total || 0,
          optionsTotal: reservationData?.optionsTotal || 0,
          language: reservationData?.language || 'fr',
          customAnswers: reservationData?.customAnswers || {},
          guestsDetails: reservationData?.guestsDetails || [],
          sessionId: reservationData?.sessionId || null,
          temporaryBookingIds: reservationData?.temporaryBookingIds || [],
          ...(reservationData?.isSplitStay ? {
            isSplitStay: true,
            splitSegments: reservationData?.splitSegments || []
          } : {}),
          createdAt: Timestamp.now()
        });
      } catch (safetyErr) {
        console.warn('⚠️ payment_safety non écrit (non bloquant):', safetyErr);
      }

      // Signaler la tentative de paiement (bascule le tracking en payment_abandoned)
      if (onPaymentAttemptStarted) onPaymentAttemptStarted();

      // 🔵 Confirmer le SetupIntent.
      // redirect: 'if_required' → 3DS en place si la banque l'accepte (la plupart),
      // redirection plein écran SEULEMENT si la banque l'exige (cartes dont l'ACS
      // refuse l'iframe ou navigateur bloquant les cookies tiers). Au retour, la page
      // ?setup_intent=...&redirect_status=succeeded est traitée dans PublicBooking.
      // inline si la banque accepte l'iframe (la majorité), redirection plein écran
      // seulement si la banque l'exige (ACS refusant l'iframe / cookies tiers bloqués).
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}${window.location.pathname}`
        },
        redirect: 'if_required'
      });

      if (confirmError) {
        console.error('❌ Erreur confirmation SetupIntent:', confirmError);

        const code = confirmError.code;
        const declineCode = confirmError.decline_code;
        if (code === 'card_declined' && declineCode === 'insufficient_funds') {
          setError(pt.errorInsufficient);
        } else if (code === 'card_declined') {
          setError(pt.errorDeclined);
        } else if (code === 'incorrect_cvc' || code === 'invalid_cvc') {
          setError(pt.errorIncorrectCvc);
        } else if (code === 'expired_card') {
          setError(pt.errorExpired);
        } else if (code === 'setup_intent_authentication_failure') {
          setError(pt.errorAuth);
        } else if (code === 'incomplete_card' || confirmError.type === 'validation_error') {
          setError(pt.errorIncomplete);
        } else {
          setError(confirmError.message || pt.errorGeneric);
        }

        deleteDoc(doc(db, 'payment_safety', setupIntentId)).catch(() => {});
        if (onPaymentError) onPaymentError();
        setProcessing(false);
        return;
      }

      // Si on arrive ici, AUCUNE redirection n'a eu lieu (cas inline / banque OK).
      // Dans le cas redirection, la page a déjà quitté et ce code ne s'exécute pas.
      if (!setupIntent || setupIntent.status !== 'succeeded') {
        console.error('❌ SetupIntent non abouti:', setupIntent?.status);
        setError(pt.errorGeneric);
        deleteDoc(doc(db, 'payment_safety', setupIntentId)).catch(() => {});
        if (onPaymentError) onPaymentError();
        setProcessing(false);
        return;
      }

      console.log('✅ SetupIntent confirmé (inline):', setupIntent.id);

      // 🔵 Récupérer les détails de la carte depuis le backend
      const pmResponse = await fetch(`${API_URL}/valetier/get-payment-method`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentMethodId: setupIntent.payment_method
        })
      });

      const pmData = await pmResponse.json();

      if (!pmData.success) {
        console.error('❌ Erreur récupération PaymentMethod:', pmData.error);
        deleteDoc(doc(db, 'payment_safety', setupIntentId)).catch(() => {});
        setError(pt.errorGeneric);
        if (onPaymentError) onPaymentError();
        setProcessing(false);
        return;
      }

      onPaymentSuccess({
        stripeCustomerId: stripeCustomerId,
        stripePaymentMethodId: setupIntent.payment_method,
        setupIntentId: setupIntentId,
        cardholderName: cardholderName,
        last4: pmData.last4,
        brand: pmData.brand,
        expiryMonth: pmData.exp_month,
        expiryYear: pmData.exp_year,
        status: 'authorized',
        amount: amount * 100
      });

      // ✅ NE PAS remettre processing à false après succès
      // Le composant sera démonté quand la réservation sera confirmée
      // Cela évite que le bouton redevienne cliquable pendant le traitement

    } catch (error) {
      console.error('❌ Erreur:', error);
      deleteDoc(doc(db, 'payment_safety', setupIntentId)).catch(() => {});
      setError(pt.errorConnection);

      if (onPaymentError) {
        onPaymentError();
      }

      // ✅ Remettre processing à false UNIQUEMENT en cas d'erreur
      setProcessing(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 p-8">
      {processing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 px-4 pointer-events-none">
          <div className="bg-white border-2 border-black rounded-xl shadow-2xl p-5 max-w-md w-full pointer-events-auto">
            <div className="flex items-start gap-3">
              <div className="text-2xl mt-0.5">🔒</div>
              <div>
                <p className="font-bold text-black text-base mb-1">{pt.threeDSTitle}</p>
                <p className="text-gray-700 text-sm">{pt.threeDSMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      <h2 className="text-2xl font-light mb-6">{pt.title}</h2>

 <div className="mb-6 bg-gray-50 p-4 rounded-lg">
        <h3 className="font-semibold mb-2">{pt.paymentSummary}</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>{pt.client}</span>
            <strong>{guestInfo.firstName} {guestInfo.name}</strong>
          </div>
          <div className="flex justify-between">
            <span>{pt.totalAmount}</span>
            <strong>{amount}€</strong>
          </div>
          <div className="flex justify-between">
            <span>{pt.preAuthRequested}</span>
            <strong className="text-green-600">{pt.noActualCharge}</strong>
          </div>
          <div className="flex justify-between">
            <span>{pt.automaticCharge}</span>
            <strong>{pt.chargeDate}</strong>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-300">
          <p className="text-xs text-gray-600">
            {pt.bankNotice}
          </p>
        </div>
      </div>


      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-5 h-5 text-green-600" />
            <span className="font-semibold">{pt.securePayment}</span>
          </div>
          <p className="text-sm text-gray-600">
            {pt.secureInfo}
          </p>
        </div>

        <div className="space-y-4">
          {isMacDesktopSafari && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              💳 <strong>{language === 'en' ? 'Payment tip' : 'Astuce paiement'}</strong> — {language === 'en'
                ? "on Safari, enter your card details manually. Autofill may reload the page and interrupt your booking. (Chrome and Firefox work perfectly.)"
                : "sur Safari, saisissez votre carte manuellement. Le remplissage automatique peut recharger la page et interrompre votre réservation. (Chrome et Firefox fonctionnent parfaitement.)"}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-2">
              {pt.cardDetails}
            </label>

            <div className="border rounded-lg p-3 bg-white">
              <PaymentElement
                options={{
                  layout: 'tabs',
                  // Carte uniquement (pré-autorisation) — comme l'ancien CardElement.
                  // Désactive Apple Pay / Google Pay (évite l'enregistrement de domaine Stripe).
                  wallets: { applePay: 'never', googlePay: 'never' },
                  defaultValues: {
                    billingDetails: {
                      name: cardholderName,
                      email: guestInfo.email
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        <div className="bg-gray-50 border border-black rounded-lg p-4">
          <p className="text-sm text-black">
            <strong>{pt.noteTitle}</strong> {pt.noteText.replace('{amount}', amount)}
          </p>
        </div>

        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            disabled={processing}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {pt.back}
          </button>
          <button
            type="submit"
            disabled={processing || !stripe}
            className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800
                       disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {processing ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                {pt.processing}
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5" />
                {pt.preAuthorize} {amount}€
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

// Composant principal avec le provider Stripe Elements.
// Le SetupIntent est créé AU MONTAGE car le Payment Element a besoin du clientSecret
// pour s'afficher.
export default function PaymentStep(props) {
  const pt = paymentTranslations[props.language || 'fr'] || paymentTranslations.fr;

  const [clientSecret, setClientSecret] = useState(null);
  const [setupInfo, setSetupInfo] = useState(null); // { setupIntentId, customerId }
  const [initError, setInitError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/valetier/create-setup-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerEmail: props.guestInfo.email,
            customerName: `${props.guestInfo.firstName} ${props.guestInfo.name}`.trim(),
            reservationId: props.reservationData?.id || Date.now().toString()
          })
        });
        const data = await resp.json();
        if (cancelled) return;
        if (!data.success) {
          console.error('❌ Erreur création SetupIntent:', data.error);
          setInitError(data.error || pt.errorGeneric);
          return;
        }
        setClientSecret(data.clientSecret);
        setSetupInfo({
          setupIntentId: data.setupIntentId || data.clientSecret.split('_secret_')[0],
          customerId: data.customerId
        });
      } catch (e) {
        if (!cancelled) {
          console.error('❌ Erreur réseau create-setup-intent:', e);
          setInitError(pt.errorConnection);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (initError) {
    return (
      <div className="bg-white border border-gray-200 p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <p className="text-sm text-red-600">{initError}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={props.onBack}
          className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          {pt.back}
        </button>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="bg-white border border-gray-200 p-8 flex items-center justify-center" style={{ minHeight: '200px' }}>
        <div className="flex items-center gap-3 text-gray-600">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black"></div>
          <span>{pt.loadingCard}</span>
        </div>
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        locale: props.language === 'en' ? 'en' : 'fr',
        appearance: {
          theme: 'stripe',
          variables: { colorPrimary: '#000000' }
        }
      }}
    >
      <PaymentForm
        {...props}
        pt={pt}
        setupIntentId={setupInfo.setupIntentId}
        stripeCustomerId={setupInfo.customerId}
      />
    </Elements>
  );
}
